import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { visuallyHiddenStyles } from '@jira/platform__accessibility/src/common/ui/screenreader-text/index.tsx';
import { useIntl, defineMessage } from '@jira/platform__react-intl';
import { transitionType } from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import type {
	Status,
	WorkflowErrorsMap,
	Transitions,
	ScopeType,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import {
	EDGE_TYPE,
	NODE_TYPE,
	GLOBAL_LOOPS_CONTAINER_TYPE,
	DIRECTION_KEYS,
	ZOOM_KEYS,
	DIAGRAM_CONTAINER_TYPE,
	STATUS_GLOBAL_TRANSITIONS_TYPE,
} from './directionKeys.tsx';
import { DEFAULT_PAN, DEFAULT_ZOOM_LEVEL } from './ellipsis.tsx';
import { getNextZoomStep } from './getExponentialZoom.tsx';
import { getFlattenedStructure, getMovedPoint } from './getMovedPoint.tsx';
import { useGlobalTransitionsCloseReason } from './GlobalTransitionsCloseReasonProvider.tsx';
import ListContainer from './ListContainer.tsx';
import ListEdge from './ListEdge.tsx';
import type {
	Positionable,
	EdgeId,
	ItemReference,
	NodeId,
	ListNodeDetails,
	ListEdgeDetails,
	ExpandableItemReference,
} from './ListEdgeDetails.tsx';
import ListNode from './ListNode.tsx';
import type { Point } from './toPortInfo.tsx';

const navigationListLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.navigation-list-label',
	defaultMessage: 'Navigate statuses and transitions in the workflow.',
	description:
		'Label for navigation list which allows keyboard users to navigation statuses and transitions in the workflow',
});

const navigationListDescription = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.navigation-list-description',
	defaultMessage: 'Use direction keys to pan diagram. Use plus and minus keys to zoom the diagram.',
	description:
		'Description for navigation list which allows keyboard users to navigation statuses and transitions in the workflow',
});

const initialTransitionListLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.initial-transition-list-label',
	defaultMessage: 'First transition.',
	description: 'Label for nested list under start node which contains the initial transition.',
});

const statusTransitionsListLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.status-transitions-list-label',
	defaultMessage: 'Transitions for {statusName}.',
	description: 'Label for nested list under status which contains transitions for that status.',
});

const startNodeMoveStartAnnouncement = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.start-node-move-start-announcement',
	defaultMessage:
		'Moving start node. Current position x: {x} y: {y}. Change position with arrow keys. Hold “shift“ to make greater movements or “alt“ to make finer movements. Confirm with “enter“ or cancel with “escape“.',
	description:
		'Announcement provided for screen readers when a move of the start node is initiated.',
});

const statusMoveStartAnnouncement = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.status-move-start-announcement',
	defaultMessage:
		'Moving {statusName} status. Current position x: {x} y: {y}. Change position with arrow keys. Hold “shift“ to make greater movements or “alt“ to make finer movements. Confirm with “enter“ or cancel with “escape“.',
	description: 'Announcement provided for screen readers when a move of a status is initiated.',
});

const globalLoopsContainerMoveStartAnnouncement = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.global-looped-transitions-container-move-start-announcement',
	defaultMessage:
		'Moving global looped transitions container. Current position x: {x} y: {y}. Change position with arrow keys. Hold “shift“ to make greater movements or “alt“ to make finer movements. Confirm with “enter“ or cancel with “escape“.',
	description:
		'Announcement provided for screen readers when a move of a global looped transitions container is initiated.',
});

const moveUpdateAnnouncement = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.move-update-announcement',
	defaultMessage: 'x: {x}, y: {y}',
	description:
		'Announcement provided for screen readers when the position of a moving item is changed.',
});

const moveCompleteAnnouncement = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.move-complete-announcement',
	defaultMessage: 'Move completed. New position x: {x} y: {y}',
	description: 'Announcement provided for screen readers when moving an item is completed.',
});

const moveCancelAnnouncement = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.move-cancel-announcement',
	defaultMessage: 'Move canceled.',
	description: 'Announcement provided for screen readers when moving an item is canceled.',
});

const editHelpText = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.edit-help-text',
	defaultMessage: 'Select to edit details.',
	description: 'Help text provided for screen readers for editing diagram items.',
});

const moveHelpText = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.move-help-text',
	defaultMessage: 'Press “m” to enter move mode.',
	description: 'Help text provided for screen readers for moving diagram items.',
});

const deleteHelpText = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.delete-help-text',
	defaultMessage: 'Select and press “delete” to delete.',
	description: 'Help text provided for screen readers for deleting diagram items.',
});

const globalLoopedTransitionsList = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.global-looped-transitions-list',
	defaultMessage: 'Global looped transitions',
	description: 'Label for nested list of global looped transitions in the workflow.',
});

const globalTransitionsButtonLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.global-transitions-button-label',
	defaultMessage: 'Any {count}, transitions from any status to {statusName}',
	description:
		'Button label for expanding a dropdown that shows multiple global transitions targeting the same status.',
});

export type Props = {
	startNodePosition: Point | undefined;
	globalLoopsContainerPosition: Point | undefined;
	statuses: Status[];
	transitions: Transitions;
	errors?: WorkflowErrorsMap;
	selectedItem?: ItemReference;
	expandedItem?: ExpandableItemReference;
	isImmutable?: boolean;
	movingItem?: Positionable;
	zoom?: number;
	pan?: Point;
	minZoom?: number;
	workflowScope: ScopeType;
	onExpandedChange?: (item: ExpandableItemReference | undefined) => void;
	onSelect?: (item: ItemReference | undefined) => void;
	onTargetChange?: (item: ItemReference | undefined) => void;
	setMovingItem?: (item: Positionable | undefined) => void;
	onItemMove?: (item: Positionable) => void;
	onZoomChange?: (zoom: number) => void;
	onPanChange?: (pan: Point) => void;
};

const EDIT_HELP_ID = 'accessible-workflow-list.edit-help';
const MOVE_HELP_ID = 'accessible-workflow-list.move-help';
const DELETE_HELP_ID = 'accessible-workflow-list.delete-help';

export const STANDARD_PAN_DISTANCE = 10;
export const GREATER_PAN_DISTANCE = 20;
export const FINE_PAN_DISTANCE = 5;

const ListNavigation = ({
	onExpandedChange,
	globalLoopsContainerPosition,
	startNodePosition,
	statuses,
	transitions,
	isImmutable = false,
	movingItem,
	errors,
	selectedItem,
	expandedItem,
	pan = DEFAULT_PAN,
	zoom = DEFAULT_ZOOM_LEVEL,
	minZoom,
	workflowScope,
	onSelect,
	onTargetChange,
	setMovingItem,
	onItemMove,
	onZoomChange,
	onPanChange,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const listRef = useRef<HTMLUListElement>(null);
	const [moveAnnouncement, setMoveAnnouncement] = useState('');

	const { nodes, globalLoops } = getFlattenedStructure(
		statuses,
		transitions,
		startNodePosition,
		errors,
		movingItem,
	);

	const handleBlur = (event: React.FocusEvent<HTMLUListElement>) => {
		if (!listRef || !listRef.current) return;

		// oxlint-disable-next-line typescript/consistent-type-assertions
		if (!event.relatedTarget || !listRef.current.contains(event.relatedTarget as Node)) {
			// focus moving outside of diagram
			onTargetChange?.(undefined);
			handleMoveCancel();
		}
	};

	const handleFocus = (event: React.FocusEvent<HTMLUListElement>) => {
		if (!listRef || !listRef.current) return;

		if (event.target === listRef.current) {
			// list itself is targeted
			onTargetChange?.({ type: DIAGRAM_CONTAINER_TYPE, id: DIAGRAM_CONTAINER_TYPE });
		}
	};

	const handleNodeFocus = (id: NodeId) => {
		onTargetChange?.({ type: NODE_TYPE, id });
	};

	const handleEdgeFocus = (id: EdgeId) => {
		onTargetChange?.({ type: EDGE_TYPE, id });
		handleMoveCancel();
	};

	const handleGlobalStatusContainerFocus = (id: string) => {
		onTargetChange?.({ type: STATUS_GLOBAL_TRANSITIONS_TYPE, id });
	};

	const handleContainerFocus = () => {
		onTargetChange?.({ type: GLOBAL_LOOPS_CONTAINER_TYPE, id: GLOBAL_LOOPS_CONTAINER_TYPE });
	};

	const handleMoveStart = (updatedMoveState: Positionable) => {
		if (updatedMoveState.type === 'start-node') {
			updateMoveAnnouncement(
				formatMessage(startNodeMoveStartAnnouncement, {
					...updatedMoveState.position,
				}),
			);
		} else if (updatedMoveState.type === GLOBAL_LOOPS_CONTAINER_TYPE) {
			updateMoveAnnouncement(
				formatMessage(globalLoopsContainerMoveStartAnnouncement, {
					...updatedMoveState.position,
				}),
			);
		} else {
			const movingStatus = statuses.find((status) => status.id === updatedMoveState.id);

			if (movingStatus) {
				updateMoveAnnouncement(
					formatMessage(statusMoveStartAnnouncement, {
						statusName: movingStatus.name,
						...updatedMoveState.position,
					}),
				);
			}
		}

		setMovingItem?.(updatedMoveState);
	};

	const handleMoveUpdate = (updatedMoveState: Positionable) => {
		updateMoveAnnouncement(
			formatMessage(moveUpdateAnnouncement, {
				...updatedMoveState.position,
			}),
		);

		setMovingItem?.(updatedMoveState);
	};

	const handleMoveComplete = () => {
		if (movingItem) {
			updateMoveAnnouncement(
				formatMessage(moveCompleteAnnouncement, {
					...movingItem.position,
				}),
			);
			onItemMove?.(movingItem);
			setMovingItem?.(undefined);
		}
	};

	const handleMoveCancel = () => {
		if (movingItem) {
			updateMoveAnnouncement(formatMessage(moveCancelAnnouncement));
			setMovingItem?.(undefined);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
		const panDirection = DIRECTION_KEYS[e.key];
		const zoomDirection = ZOOM_KEYS[e.key];

		if (panDirection && !movingItem && pan) {
			let moveDistance = STANDARD_PAN_DISTANCE;
			if (e.shiftKey) {
				moveDistance = GREATER_PAN_DISTANCE;
			} else if (e.altKey) {
				moveDistance = FINE_PAN_DISTANCE;
			}

			const updatedPan = getMovedPoint(pan, panDirection, moveDistance);
			onPanChange?.(updatedPan);
		}

		if (zoomDirection) {
			const nextZoom = getNextZoomStep(zoom, zoomDirection, minZoom);
			onZoomChange?.(nextZoom);
		}
	};

	// oxlint-disable-next-line react/preserve-manual-memoization
	const updateMoveAnnouncement = useMemo(
		() => debounce((message: string) => setMoveAnnouncement(message), 500),
		[],
	);

	// Cancel debounced function when component unmounts
	useEffect(
		() => () => {
			updateMoveAnnouncement.cancel();
		},
		[updateMoveAnnouncement],
	);

	return (
		<HiddenWrapper>
			{!isImmutable && (
				<>
					<div id={EDIT_HELP_ID}>{formatMessage(editHelpText)}</div>
					<div id={MOVE_HELP_ID}>{formatMessage(moveHelpText)}</div>
					<div id={DELETE_HELP_ID}>{formatMessage(deleteHelpText)}</div>
				</>
			)}
			<VisuallyHidden id="accessible-workflow-diagram.navigationList-description">
				{formatMessage(navigationListDescription)}
			</VisuallyHidden>
			<div aria-live="assertive" data-testid="accessible-workflow-diagram.move-announcement">
				{moveAnnouncement}
			</div>
			<List
				ref={listRef}
				onBlur={handleBlur}
				onFocus={handleFocus}
				aria-label={formatMessage(navigationListLabel)}
				aria-describedby="accessible-workflow-diagram.navigationList-description"
				onKeyDown={handleKeyDown}
				tabIndex={0}
			>
				{nodes.map((node) => (
					<li key={node.id}>
						<ListNode
							node={node}
							isImmutable={isImmutable}
							selectedItem={selectedItem}
							describedBy={getNodeDescribedBy(isImmutable, node)}
							workflowScope={workflowScope}
							onSelect={onSelect}
							onFocus={handleNodeFocus}
							onMoveStart={handleMoveStart}
							onMoveUpdate={handleMoveUpdate}
							onMoveComplete={handleMoveComplete}
							onMoveCancel={handleMoveCancel}
						/>

						<NodeChildren
							node={node}
							statuses={statuses}
							isImmutable={isImmutable}
							selectedItem={selectedItem}
							expandedItem={expandedItem}
							onSelect={onSelect}
							onFocus={handleEdgeFocus}
							onExpandedChange={onExpandedChange}
							handleGlobalStatusContainerFocus={handleGlobalStatusContainerFocus}
						/>
					</li>
				))}
				{globalLoops && globalLoops.length > 0 && (
					<li>
						<List aria-label={formatMessage(globalLoopedTransitionsList)}>
							<li key="global-loops-container">
								<ListContainer
									position={
										movingItem?.type === 'global-loops-container'
											? movingItem.position
											: globalLoopsContainerPosition
									}
									isImmutable={isImmutable}
									isMoving={movingItem?.type === GLOBAL_LOOPS_CONTAINER_TYPE}
									onFocus={handleContainerFocus}
									onMoveStart={handleMoveStart}
									onMoveUpdate={handleMoveUpdate}
									onMoveComplete={handleMoveComplete}
									onMoveCancel={handleMoveCancel}
								/>
							</li>
							{globalLoops.map((edge) => (
								<li key={edge.id}>
									<ListEdge
										edge={edge}
										statuses={statuses}
										isImmutable={isImmutable}
										selectedItem={selectedItem}
										onSelect={onSelect}
										onFocus={handleEdgeFocus}
									/>
								</li>
							))}
						</List>
					</li>
				)}
			</List>
		</HiddenWrapper>
	);
};

const NodeChildren = ({
	node,
	statuses,
	isImmutable,
	selectedItem,
	expandedItem,
	onSelect,
	onFocus,
	onExpandedChange,
	handleGlobalStatusContainerFocus,
}: {
	node: ListNodeDetails;
	statuses: Status[];
	isImmutable: boolean;
	selectedItem: ItemReference | undefined;
	expandedItem?: ExpandableItemReference;
	onSelect?: (item: ItemReference | undefined) => void;
	onFocus?: (id: EdgeId) => void;
	handleGlobalStatusContainerFocus?: (id: string) => void;
	onExpandedChange?: (item: ExpandableItemReference | undefined) => void;
}) => {
	const { formatMessage } = useIntl();
	const { consumeKeyboardClose } = useGlobalTransitionsCloseReason();

	const isExpanded =
		expandedItem?.type === STATUS_GLOBAL_TRANSITIONS_TYPE && expandedItem?.id === node.status?.id;
	const globalTransitionsButtonRef = useRef<HTMLButtonElement>(null);
	const wasExpandedRef = useRef(isExpanded);
	// On keyboard (Tab) close, return focus to the opener button; skip pointer closes so clicking
	// elsewhere does not steal focus back. Defer a frame to run after ADS's popup focus teardown.
	useEffect(() => {
		if (wasExpandedRef.current && !isExpanded && consumeKeyboardClose()) {
			// Advance the ref only once the frame fires so a cancelled frame can reschedule focus.
			const frameId = globalThis.requestAnimationFrame(() => {
				wasExpandedRef.current = isExpanded;
				globalTransitionsButtonRef.current?.focus();
			});
			return () => globalThis.cancelAnimationFrame(frameId);
		}
		wasExpandedRef.current = isExpanded;
	}, [consumeKeyboardClose, isExpanded]);

	if (!node.children || node.children.length === 0) {
		return null;
	}

	const globalEdges = node.children.filter((edge) => edge.type === transitionType.GLOBAL);
	const otherEdges = node.children.filter((edge) => edge.type !== transitionType.GLOBAL);

	const handleButtonClick = () => {
		if (node.status?.id) {
			onExpandedChange?.({ type: STATUS_GLOBAL_TRANSITIONS_TYPE, id: node.status.id });
		}
	};

	const handleButtonFocus = () => {
		if (node.status?.id) {
			handleGlobalStatusContainerFocus?.(node.status.id);
		}
	};

	return (
		<List
			aria-label={
				node.status
					? formatMessage(statusTransitionsListLabel, {
							statusName: node.status.name,
						})
					: formatMessage(initialTransitionListLabel)
			}
		>
			{globalEdges.length > 1 ? (
				<li>
					<button
						ref={globalTransitionsButtonRef}
						onClick={handleButtonClick}
						onFocus={handleButtonFocus}
						type="button"
						aria-haspopup="menu"
						aria-expanded={isExpanded}
					>
						{formatMessage(globalTransitionsButtonLabel, {
							statusName: node.status?.name,
							count: globalEdges.length,
						})}
					</button>
				</li>
			) : (
				<>
					{globalEdges.map((edge) => (
						<li key={edge.id}>
							<ListEdge
								edge={edge}
								statuses={statuses}
								isImmutable={isImmutable}
								selectedItem={selectedItem}
								describedBy={getTransitionDescribedBy(isImmutable, edge)}
								onSelect={onSelect}
								onFocus={onFocus}
							/>
						</li>
					))}
				</>
			)}
			{otherEdges.map((edge) => (
				<li key={edge.id}>
					<ListEdge
						edge={edge}
						statuses={statuses}
						isImmutable={isImmutable}
						selectedItem={selectedItem}
						describedBy={getTransitionDescribedBy(isImmutable, edge)}
						onSelect={onSelect}
						onFocus={onFocus}
					/>
				</li>
			))}
		</List>
	);
};

const getNodeDescribedBy = (isImmutable: boolean, node: ListNodeDetails) => {
	if (isImmutable) {
		return undefined;
	}

	if (node.isInitial) {
		return MOVE_HELP_ID;
	}

	if (node.canDelete) {
		return `${EDIT_HELP_ID} ${MOVE_HELP_ID} ${DELETE_HELP_ID}`;
	}

	return `${EDIT_HELP_ID} ${MOVE_HELP_ID}`;
};

const getTransitionDescribedBy = (isImmutable: boolean, edge: ListEdgeDetails) => {
	if (isImmutable) {
		return undefined;
	}

	if (edge.canDelete) {
		return `${EDIT_HELP_ID} ${DELETE_HELP_ID}`;
	}

	return EDIT_HELP_ID;
};

export default ListNavigation;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ul({
	listStyle: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HiddenWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	...visuallyHiddenStyles,
});
